import type { FunctionComponent } from "react";
import { formatDate } from "common/helpers";
import Address from "components/shared/Address";
import Profile from "components/shared/Profile";
import type { FormattedDirector } from "../../../helpers";
import useDirectorInfoForm from "./useDirectorInfoForm";
import { Field, Form, FormTheme, RowFields } from "@kanda-libs/ks-component-ts";
import DirectorAddressForm from "./DirectorAddressForm";
import { Button } from "@kanda-libs/ks-design-library";
import { UserType } from "@kanda-libs/ks-frontend-services";

export interface DirectorInfoProps {
  index: number;
  director?: FormattedDirector;
  user?: UserType;
  editing?: boolean;
  onEdit: (index: number, director: UserType) => void;
}

const DirectorInfo: FunctionComponent<DirectorInfoProps> = function ({
  index,
  director,
  user,
  editing = false,
  onEdit,
}) {
  const { form, onSubmit } = useDirectorInfoForm(index, user, onEdit);

  const sharedPropertyProps = {
    clickToCopy: !editing,
  };

  const dateOfBirth = director?.dateOfBirth
    ? formatDate(director?.dateOfBirth).date
    : "Not added";

  return (
    <FormTheme variant="streamline">
      <Form id="staff-edit-director" form={form} onSubmit={onSubmit}>
        <Profile.Property
          label="Director name"
          value={
            editing ? (
              <RowFields>
                <Field.Input name="first_name" />
                <Field.Input name="last_name" />
              </RowFields>
            ) : (
              director?.name
            )
          }
          {...sharedPropertyProps}
        />
        <Profile.Property
          label="Director email"
          value={editing ? <Field.Input name="email" /> : director?.email}
          {...sharedPropertyProps}
        />
        <Profile.Property
          label="Director phone"
          value={editing ? <Field.Input name="mobile" /> : director?.phone}
          {...sharedPropertyProps}
        />
        <Profile.Property
          label="Director DOB"
          value={
            editing ? (
              <Field.DatePickerInput name="director_info.date_of_birth" />
            ) : (
              dateOfBirth
            )
          }
          {...sharedPropertyProps}
        />
        <Profile.Property label="Director address" {...sharedPropertyProps}>
          <>
            {!editing && <Address address={director?.address} />}
            {editing && <DirectorAddressForm />}
          </>
        </Profile.Property>
        {editing && (
          <Button.Text id="staff-director-info-submit" submit label="Submit" />
        )}
      </Form>
    </FormTheme>
  );
};

export default DirectorInfo;
